import React, { useEffect } from 'react';

import { Helmet } from 'react-helmet-async';

import { StyledPrivacyPolicyPage } from './styles';
import { TextLinkColor } from '../../../components/atoms/text-link-color';
import { bemClassName } from '../../../utils/bem';
import { SUPPORT_EMAIL } from '../../../utils/constants';
import { canonical, ogImage, ogUrl } from '../../../utils/seo';

const b = bemClassName('privacy-policy-page');

const PrivacyPolicyPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <StyledPrivacyPolicyPage>
      <Helmet>
        <title>Privacy Policy — How We Protect Your Data | Active Shape</title>
        <meta
          name="description"
          content="Read the Active Shape privacy policy to understand how we collect, use, and protect your personal data."
        />
        {canonical('/privacy-policy') && <link rel="canonical" href={canonical('/privacy-policy')} />}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Privacy Policy — How We Protect Your Data | Active Shape" />
        <meta
          property="og:description"
          content="Read the Active Shape privacy policy to understand how we collect, use, and protect your personal data."
        />
        {ogImage() && <meta property="og:image" content={ogImage()} />}
        {ogUrl('/privacy-policy') && <meta property="og:url" content={ogUrl('/privacy-policy')} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy — How We Protect Your Data | Active Shape" />
        <meta
          name="twitter:description"
          content="Read the Active Shape privacy policy to understand how we collect, use, and protect your personal data."
        />
        {ogImage() && <meta name="twitter:image" content={ogImage()} />}
      </Helmet>
      <div className={b()}>
        <div className={b('card')}>
          <h1 className={b('title')}>ActiveShape Privacy Policy</h1>
          <p className={b('update')}>Effective Date: December 11, 2024 — Last updated: March 2026</p>

          <div className={b('nav-links')}>
            <TextLinkColor to="/terms-of-service">Terms of Service</TextLinkColor>
            <TextLinkColor to="/referral-terms">Referral Program Terms</TextLinkColor>
            <TextLinkColor to="/user-data-deletion">Data Deletion Policy</TextLinkColor>
          </div>

          <p>
            At <strong>ActiveShape</strong>, operated by <strong>Tower Tech</strong>, we respect your privacy and are
            committed to protecting the personal information you share with us. This Policy describes how we collect,
            use, and protect your data.
          </p>

          <h2 className={b('section-title')}>1. Information We Collect</h2>
          <p>We collect information you provide and automatic usage data:</p>
          <ul>
            <li>Name and email address</li>
            <li>Profile picture (if you sign in with Google, Facebook, or Twitter)</li>
            <li>
              Health and fitness data you voluntarily enter (weight, height, age, gender, fitness goals, activity level,
              meal logs, fasting schedules, and workout history)
            </li>
            <li>
              Photos from your device — only when you explicitly grant permission to access your photo library to upload
              meal images or a profile picture
            </li>
            <li>
              Camera — only when you explicitly grant permission to take photos directly within the app (e.g., meal
              photos)
            </li>
            <li>
              Payment information processed by our payment provider, Stripe. We do not store card numbers or CVV codes
            </li>
            <li>Usage and device data (device type, operating system, app version, crash reports, session activity)</li>
            <li>
              Masked recordings of your session on the web platform — the pages you visit and where you click, with all
              text and media hidden
            </li>
            <li>Cookies and similar technologies (web platform only)</li>
            <li>
              Referral program data — if you participate in the ActiveShape Referral Program, we collect your referral
              code, the number of users you have referred, referral statuses, commission amounts, payout records, and IP
              address at the time of referred user signup (for fraud prevention)
            </li>
          </ul>

          <h2 className={b('section-title')}>2. How We Use Information</h2>
          <ul>
            <li>To create and manage your account</li>
            <li>Personalize your health and fitness experience</li>
            <li>Generate meal plans, workout recommendations, and insights based on your goals</li>
            <li>Process payments and manage your subscription</li>
            <li>Improve the quality of our service</li>
            <li>Send updates and notifications</li>
            <li>
              Operate the Referral Program, calculate commissions, process payouts, and detect fraudulent activity
            </li>
            <li>Comply with legal requirements</li>
          </ul>

          <h2 className={b('section-title')}>3. Legal Basis</h2>
          <p>
            We process your data based on your explicit consent, the performance of a contract with you, or our
            legitimate interest in improving our services.
          </p>

          <h2 className={b('section-title')}>4. How We Share Data</h2>
          <p>
            We do not sell your personal information. We only share data with service providers strictly necessary to
            operate the application:
          </p>
          <ul>
            <li>
              <strong>Stripe</strong> — payment processing, subscription management, and referral program payouts via
              Stripe Connect Express (which handles identity verification and tax form generation for referrers)
            </li>
            <li>
              <strong>Amazon Web Services (AWS)</strong> — cloud infrastructure and file storage
            </li>
            <li>
              <strong>Google / Facebook / Twitter</strong> — social sign-in (only when you choose to use these
              providers)
            </li>
            <li>
              <strong>Spoonacular</strong> — recipe and nutrition data
            </li>
            <li>
              <strong>OpenAI</strong> — AI-powered meal and workout recommendations
            </li>
            <li>
              <strong>Sentry</strong> — error monitoring and session recording on the web platform. Recordings capture
              which pages you visit and where you click, so we can find and fix problems in the product. Text content
              and media are masked before a recording leaves your browser, so the health information you enter is not
              included
            </li>
            <li>
              <strong>Google Analytics</strong> — aggregated usage statistics on the web platform
            </li>
          </ul>

          <h2 className={b('section-title')}>5. Permissions</h2>
          <p>The ActiveShape mobile app may request the following device permissions:</p>
          <ul>
            <li>
              <strong>Camera</strong> — to take photos of meals or a profile picture. Requested only when you initiate a
              photo action. You can deny this permission without losing core app functionality.
            </li>
            <li>
              <strong>Photo Library</strong> — to select existing photos from your device for meal logging or your
              profile. Requested only when you initiate a photo selection. You can deny this permission without losing
              core app functionality.
            </li>
          </ul>

          <h2 className={b('section-title')}>6. Security</h2>
          <p>
            We implement technical and organizational measures to protect your data, including SSL/TLS encryption,
            restricted access controls, httpOnly cookies for authentication tokens, and security monitoring.
          </p>

          <h2 className={b('section-title')}>7. Retention</h2>
          <p>
            We retain your data while your account is active or as needed to comply with legal obligations. You can
            request deletion at any time. See our <a href="/user-data-deletion">Data Deletion Policy</a> for full
            details.
          </p>

          <h2 className={b('section-title')}>8. Your Rights</h2>
          <p>You have the right to request access, rectification, or deletion of your data. You may also:</p>
          <ul>
            <li>Export your data</li>
            <li>Withdraw consent at any time</li>
            <li>Delete your account from Settings → Account → Delete my account</li>
          </ul>
          <p>
            To exercise these rights, contact us at: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </p>

          <h2 className={b('section-title')}>9. Cookies</h2>
          <p>
            On the web platform, we use cookies for authentication (httpOnly secure cookies), analytics, and application
            functionality. You can manage cookie preferences through your browser settings.
          </p>

          <h2 className={b('section-title')}>10. Referral Program Data</h2>
          <p>
            If you participate in our Referral Program, we collect and process additional data to operate the program
            and prevent fraud:
          </p>
          <ul>
            <li>Your unique referral code and optional custom slug</li>
            <li>
              The number and status of users you have referred (anonymized — referrers do not receive personally
              identifiable information about referred users beyond initials and subscription status)
            </li>
            <li>Commission amounts, payout records, and Stripe Connect account information</li>
            <li>
              IP addresses of referred users at the time of signup (used solely for fraud detection, not shared with
              referrers)
            </li>
            <li>Aggregated, anonymized referral analytics used for system-wide reporting</li>
          </ul>
          <p>
            Referral program data is retained as long as your account is active. If you withdraw from the program,
            payout records may be retained for tax compliance purposes as required by applicable law. For full details,
            see our <a href="/referral-terms">Referral Program Terms</a>.
          </p>

          <h2 className={b('section-title')}>11. Children</h2>
          <p>
            ActiveShape is not directed at children under 13. We do not intentionally collect data from minors. If you
            believe a minor has provided us with personal information, please contact us immediately.
          </p>

          <h2 className={b('section-title')}>12. Changes</h2>
          <p>
            We may update this policy. The most recent version will always be available at{' '}
            <strong>https://www.active-shape.com/privacy-policy</strong>. We will notify you of significant changes by
            email or through an in-app notice.
          </p>

          <h2 className={b('section-title')}>13. Contact</h2>
          <p>
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </p>

          <div className={b('footer')}>
            <p>Related documents:</p>
            <div className={b('footer', 'links')}>
              <TextLinkColor to="/terms-of-service">Terms of Service</TextLinkColor>
              <TextLinkColor to="/referral-terms">Referral Program Terms</TextLinkColor>
              <TextLinkColor to="/user-data-deletion">Data Deletion Policy</TextLinkColor>
            </div>
          </div>
        </div>
      </div>
    </StyledPrivacyPolicyPage>
  );
};

export default PrivacyPolicyPage;
